'use client';

// RealtimeLotsProvider — the source-agnostic consumer contract for the admin
// lot-state stream (ADR 042 §§4–5, BL-1251). Owns the LotStateStore (subscription,
// LWW-per-lot fold, cursor, cold-seed + reconcile catch-up) and the wake/reconnect
// triggers; consumers read `useLots()` / `useLot(id)` and hold NONE of that
// plumbing (AC 4). One provider serves both the Clerk Screen and the Ordering
// page — each selects the slice it renders.
//
// The transport lives entirely behind this provider, so the eventual move off
// Supabase Realtime (AlloyDB / authenticated SSE — a separate project) is an
// adapter swap with zero consumer changes. This is the primary reason the
// abstraction exists.

import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useSyncExternalStore,
    type ReactNode,
} from 'react';

import type { AdminLotStateDeltaDecoded } from '../../mappers/realtime/admin-lot-state';
import type { RealtimeSubscriber } from '../../types/realtime/subscriber';

import { installReconcileTriggers } from '../../stores/realtime/install-reconcile-triggers';
import {
    DISCONNECTED_LOT_STATE,
    LotStateStore,
    type LotStateCatchupFetcher,
    type LotStateConnection,
} from '../../stores/realtime/lot-state-store';
import type { NotifyScheduler } from '../../stores/realtime/entity-state-store';

/**
 * NO periodic reconcile for this stream (BL-1628 AC 6, ADR 053 — Console Scale § 6.2).
 *
 * The 30 s tick this replaces existed for one reason: the fold could not tell a
 * dropped delta from a quiet lot, so the only defence was to re-read the whole
 * auction on a timer — in every open tab, whether or not anything had changed. On a
 * busy sale that re-sent what the live stream had already delivered (~300 changed
 * lots per 30 s at 10 bids/sec); on a quiet one it asked for nothing, repeatedly.
 *
 * The per-auction sequence makes a hole visible on the very next delta, so catch-up
 * now runs when something was ACTUALLY missed — approximately never on a healthy
 * socket, and faster than a timer when a message is genuinely lost.
 *
 * The other four reconcile triggers are UNCHANGED (RAC 3): focus, visibilitychange,
 * online, and transport reconnect all still force a sweep. `installReconcileTriggers`
 * reads `0` as "install no interval" and leaves those listeners alone.
 *
 * Do NOT restore a value here as a "safety net" without first sequencing whatever
 * stream prompted it — a timer on a gap-detecting stream is pure duplicate load.
 */
const RECONCILE_INTERVAL_MS = 0;

/** Stable empty map for SSR / no-provider server snapshots (ref-stable). */
const EMPTY_LOTS: ReadonlyMap<string, AdminLotStateDeltaDecoded> = new Map();

const LotStateStoreContext = createContext<LotStateStore | null>(null);

/**
 * What this provider covers. Exactly one of the two, expressed as a union so
 * "both" and "neither" are unrepresentable rather than merely wrong.
 *
 * `lotId` is SINGULAR, and that is a deliberate limit rather than an unfinished
 * one (BL-1542 AC 35). A list could name lots in two different auctions, and
 * this stream's topic is derived from a single auction — so there would be no
 * topic to subscribe to. The type refuses the request instead of the route
 * rejecting it later.
 */
type RealtimeLotsScopeProps =
    | {
          /** The auction whose whole lot-state catalog to fold. */
          auctionId: string;
          lotId?: undefined;
      }
    | {
          /**
           * Fold ONE lot (BL-1542 AC 35) — the bid record mounted off the clerk
           * console, which knows its lot and nothing else. The seed is narrowed
           * server-side (`?lot_id=`, O(1) instead of the auction's catalog); the
           * SUBSCRIPTION is still the auction's topic, and the store keeps only
           * this lot's deltas off it.
           */
          lotId: string;
          auctionId?: undefined;
      };

interface RealtimeLotsProviderOwnProps {
    /** Transport subscriber (shared with the app's RealtimeStack). */
    subscriber: RealtimeSubscriber;
    /** Authenticated admin lot-state catch-up fetcher (mints the topic + cold-seed). */
    catchupFetcher: LotStateCatchupFetcher;
    /** Override the reconcile interval. Defaults to 0 — no timer (BL-1628 AC 6). */
    reconcileIntervalMs?: number;
    /**
     * BUG-2100: `false` holds the store unstarted — no catch-up, no subscription,
     * no reconcile triggers — and provides no store, as if none were mounted. It
     * exists so a surface can stop and start its feed (the admin phone app's
     * offline mode, ADR 066 — Offline Mode § 6.2) without changing the element it
     * renders: a different element would remount every child. Defaults to `true`.
     */
    enabled?: boolean;
    /**
     * Override the coalesced-notify scheduler (BUG-1407). Same test-seam role
     * as `reconcileIntervalMs`: production takes the default short-timer
     * window, and a test injects a manual pump so "one render per burst" is an
     * exact assertion rather than a race against a timer.
     */
    scheduleNotify?: NotifyScheduler;
    reconcileThrottleMs?: number;
    children: ReactNode;
}

export type RealtimeLotsProviderProps = RealtimeLotsScopeProps & RealtimeLotsProviderOwnProps;

/**
 * Mount around any subtree that renders admin lot state — for a whole auction
 * (the clerk console's rail and ordering page) or for ONE lot (BL-1542: the bid
 * record mounted off-console). Owns the store; `useLots` / `useLot` read it.
 */
export function RealtimeLotsProvider({
    auctionId,
    lotId,
    subscriber,
    catchupFetcher,
    reconcileIntervalMs = RECONCILE_INTERVAL_MS,
    reconcileThrottleMs,
    scheduleNotify,
    enabled = true,
    children,
}: RealtimeLotsProviderProps) {
    const store = useMemo(
        () =>
            new LotStateStore(
                subscriber,
                catchupFetcher,
                // The union above guarantees exactly one is set; this narrows it
                // back to that union for the store.
                lotId != null ? { lotId } : { auctionId: auctionId as string },
                scheduleNotify,
            ),
        // `lotId` and `auctionId` are BOTH dependencies. A store is bound to its
        // scope for life, so a scope change must build a new one — which is what
        // re-seeds (RAC 21). Leaving either out would keep the old store, and the
        // new lot would render as though it had no state: correct-looking, empty,
        // and silent. That is BUG-1467's failure shape one layer up.
        // `enabled` too (BUG-2100): each enabled period gets a fresh store that
        // cold-seeds, as a fresh mount would, rather than a stopped store's
        // stale fold from before the gap.
        [subscriber, catchupFetcher, auctionId, lotId, scheduleNotify, enabled],
    );

    useEffect(() => {
        if (!enabled) return;
        void store.start();
        return () => store.stop();
    }, [store, enabled]);

    useEffect(() => {
        if (!enabled) return;
        return installReconcileTriggers({
            reconcileAll: () => store.reconcile(),
            subscriber,
            intervalMs: reconcileIntervalMs,
            reconcileThrottleMs,
        });
    }, [store, subscriber, reconcileIntervalMs, reconcileThrottleMs, enabled]);

    return (
        <LotStateStoreContext.Provider value={enabled ? store : null}>
            {children}
        </LotStateStoreContext.Provider>
    );
}

function useLotStateStore(): LotStateStore {
    const store = useContext(LotStateStoreContext);
    if (!store) {
        throw new Error('useLots / useLot must be used inside a <RealtimeLotsProvider>');
    }
    return store;
}

/**
 * The same store, or `null` when no `<RealtimeLotsProvider>` is mounted above.
 *
 * The non-throwing twin of `useLotStateStore`, and the seam
 * `useLotStateForRecord` uses to answer "am I on the clerk console?" without a
 * prop (BL-1542 AC 29, ADR 053 — Console Scale § 4.1). The throwing version
 * stays the default for `useLots` / `useLot`, whose callers genuinely require
 * the auction fold — absence there is a wiring bug and should be loud.
 *
 * Exported rather than exporting the context itself so the context stays
 * module-private and there is exactly one way to read it.
 *
 * @returns the auction's lot-state store, or `null` off-console.
 */
export function useOptionalLotStateStore(): LotStateStore | null {
    return useContext(LotStateStoreContext);
}

/** Read the folded lot map (the `useSyncExternalStore` seam). */
function useLotMap(): ReadonlyMap<string, AdminLotStateDeltaDecoded> {
    const store = useLotStateStore();
    const subscribe = useCallback(
        (listener: () => void) => store.subscribe(listener),
        [store],
    );
    const getSnapshot = useCallback(() => store.getLots(), [store]);
    return useSyncExternalStore(subscribe, getSnapshot, () => EMPTY_LOTS);
}

export interface UseLotsOptions<T> {
    /**
     * Project each lot to only the slice the consumer renders (ordering reads
     * `{ number, suffix, ring }`; the clerk reads the full scalar set). Wrap in
     * `useCallback` to avoid recomputing every render. Omit to get the full
     * `AdminLotStateDeltaDecoded`.
     */
    select?: (lot: AdminLotStateDeltaDecoded) => T;
    /** Sort comparator over the raw deltas (e.g. by `closeTime`). */
    sortBy?: (a: AdminLotStateDeltaDecoded, b: AdminLotStateDeltaDecoded) => number;
}

/**
 * The auction's lots, folded live, projected + sorted to the consumer's shape.
 * The consumer holds NO subscription / fold / catch-up / transport logic — all
 * of that lives in the provider's store (AC 4).
 */
export function useLots<T = AdminLotStateDeltaDecoded>(
    opts: UseLotsOptions<T> = {},
): T[] {
    const map = useLotMap();
    const { select, sortBy } = opts;
    return useMemo(() => {
        const rows = [...map.values()];
        if (sortBy) rows.sort(sortBy);
        return (select ? rows.map(select) : (rows as unknown as T[]));
    }, [map, select, sortBy]);
}

/**
 * Whether the lot-state stream is established (BL-1254 AC 8).
 *
 * Exposed because a consumer CANNOT infer this from `useLots()` — an auction
 * with zero lots is indistinguishable from a dead subscription by lot count
 * alone. `hasSeeded` is sticky, so a consumer can tell "still connecting"
 * (`!hasSeeded`) from "dropped after connecting" (`hasSeeded && !isSubscribed`)
 * — the distinction a connection banner needs to avoid crying wolf on first paint.
 */
export function useLotsConnection(): LotStateConnection {
    const store = useLotStateStore();
    const subscribe = useCallback(
        (listener: () => void) => store.subscribe(listener),
        [store],
    );
    const getSnapshot = useCallback(() => store.getConnection(), [store]);
    return useSyncExternalStore(subscribe, getSnapshot, () => DISCONNECTED_LOT_STATE);
}

/**
 * One lot's current folded state, or `undefined` until it lands. `null` lotId
 * yields `undefined` (stable hook order for conditional consumers).
 */
export function useLot(lotId: string | null): AdminLotStateDeltaDecoded | undefined {
    const map = useLotMap();
    return useMemo(
        () => (lotId ? map.get(lotId) : undefined),
        [map, lotId],
    );
}
